import * as cx from 'classnames';
import values from 'lodash-es/values';
import { FormControls } from 'mk/forms/FormControls';
import * as React from 'react';

interface OwnProps {
    className?: string;

    fieldErrors?: any;
    formErrors?: string[];
    isSubmitting: boolean;

    onSubmit?(fieldValues, form);
    onError?({ fieldValues, fieldErrors, formErrors }, form);
    onClean?(fieldValues, fieldErrors, form);
}

interface FormElementProps {
    name: string;
    autofocus: boolean;
    form: any;
    isSubmitting: boolean;
    errors: string[];
    children: React.ReactChildren;

    ref(r);
}

interface FormElement {
    props: FormElementProps;
    validate(form);
    setFocus();
    clear();
}

export class RawForm extends React.Component<OwnProps> {

    private autofocusElement: FormElement;
    private elementRefs: { [key: string]: FormElement } = {};

    public componentDidMount() {
        if (this.autofocusElement) {
            this.autofocusElement.setFocus();
        }
    }

    public render() {
        const { className } = this.props;

        let errorsList;
        if (this.props.formErrors && this.props.formErrors.length) {
            errorsList = (
                <ul className="g-form-errors">
                    {this.props.formErrors.map((error, index) => (
                        <li key={index} className="form-error">{error}</li>
                    ))}
                </ul>
            );
        }

        return (
            <form
                className={cx('g-form', className)}
                onSubmit={this.onSubmit}
                noValidate
            >
                {errorsList}
                {this.renderChildren()}
            </form>
        );
    }

    public clear() {
        values(this.elementRefs).forEach((field) => {
            field.clear();
        });
    }

    /**
     * We are already using refs internaly here, so if some component is using
     * form needs a reference, it needs to use this function.
     * @arg {string} name - name of the element, needs to be defined as
     *     'name' property on the field
     * @return {React.Component} - reference to component/field
     */
    public getFieldRef(name) {
        return this.elementRefs[name];
    }

    // TODO: if refs and keys are defined outside, when creating Form, they are probably
    // not preserved: https://facebook.github.io/react/docs/clone-with-props.html#reactelement-react.addons.clonewithpropsreactelement-element-object-extraprops
    private renderChildren() {
        const { fieldErrors } = this.props;
        const addFormProps = (element) => {
            // element can be null if you use {condition ? <span></span> : null}
            if (!React.isValidElement<FormElementProps>(element)) {
                // just skip
                return element;
            }

            // isFormField is a function, but it's existence is enough to know it is
            // form field (there is no component with isFormField() == false)
            if ((element.type as any).isFormField) {
                const errors = fieldErrors && fieldErrors[element.props.name];
                const addProps = {
                    form: this,
                    isSubmitting: this.props.isSubmitting,
                    // api changed but this should be backward compatible
                    errors: errors && (Array.isArray(errors) ? errors : [errors]),
                    ref: (r) => {
                        if (element.props.autofocus && !this.autofocusElement) {
                            this.autofocusElement = r;
                        }
                        this.elementRefs[element.props.name] = r;
                    },
                };

                return React.cloneElement(element, addProps);
            }

            if ((element.type as any) === FormControls) {
                const addProps = {
                    form: this,
                    isSubmitting: this.props.isSubmitting,
                };
                return React.cloneElement(element, addProps);
            }

            // if elment has children recursively find formFields
            if (element.props.children) {
                const newChildren = React.Children.map(element.props.children, addFormProps);
                return React.cloneElement(element, {children: newChildren});
            }
            return element;
        };

        return React.Children.map(this.props.children, addFormProps);
    }

    /**
     * @return {object} - {fieldValues, fieldErrors, formErrors}
     */
    private validateForm = () => {
        const fieldValues = {};
        const fieldErrors = {};

        values(this.elementRefs).forEach((field) => {
            const [ value, errors ] = field.validate(this);
            fieldValues[field.props.name] = value;
            if (errors) {
                fieldErrors[field.props.name] = errors;
            }
        });

        const formErrors = [];

        if (this.props.onClean) {
            const afterClean = this.props.onClean(fieldValues, fieldErrors, this);
            // onClean can return only part of the form state as a convenience
            return {
                fieldValues,
                fieldErrors,
                formErrors,
                ...afterClean,
            };
        }

        return { fieldValues, fieldErrors, formErrors };
    };

    private onSubmit = (event) => {
        event.preventDefault();

        const { fieldValues, fieldErrors, formErrors } = this.validateForm();

        if (values(fieldErrors).length || formErrors.length) {
            if (this.props.onError) {
                this.props.onError({fieldValues, fieldErrors, formErrors}, this);
            }
        } else {
            if (this.props.onSubmit) {
                this.props.onSubmit(fieldValues, this);
            }
        }
    };
}
